/**
 * Audio playback session — encapsulates the HTMLAudioElement lifecycle.
 *
 * Owns the audio element + every listener (loadedmetadata, canplaythrough,
 * timeupdate, ended, error). The consumer never touches the element
 * directly; it interacts via the returned controller:
 *
 *   const session = createAudioSession(url, callbacks, opts);
 *   session.pause();
 *   session.seekTo(45);
 *   session.stop();
 *
 * Generation guard: when the caller fires multiple loads in quick
 * succession (user clicks Listen twice), pass `isStale` that returns
 * true once a newer generation has started. Stale callbacks become
 * no-ops automatically — kills the player-flicker race that hit the
 * pre-Phase-2 single-article.js.
 *
 * Intentional stop: `stop()` flags an internal sentinel BEFORE clearing
 * `src=''`. Without it, the `error` event the browser fires when src is
 * blanked was triggering the consumer's error path. The flag is read
 * by the listener and silently ignored.
 *
 * Build path:
 *   src   assets/js/src/single-article/audio-session.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/single-article/audio-session.js  (attaches window.XOT.article.audioSession)
 *
 * @since Phase 2 — JS modernization, bite 12
 */

const NEAR_END_GUARD = 0.25;   // seconds — error fired this close to duration is "natural" end, ignored

export interface AudioSessionCallbacks {
    /** Fires once on canplaythrough → first play() success (not on resume). */
    onReady?:      () => void;
    /** Fires from native timeupdate (~4 Hz). */
    onTimeUpdate?: (currentTime: number, duration: number) => void;
    /** Fires when the audio reaches its natural end. */
    onEnded?:      () => void;
    /** Fires on load/decode error (NOT on intentional stop). */
    onError?:      () => void;
    /** Fires after stop() has cleaned up — last hook for UI reset. */
    onStopped?:    () => void;
}

export interface AudioSessionOptions {
    /** Initial playbackRate. Defaults to 1. */
    rate?:   number;
    /** Initial volume 0..1. Defaults to 1. */
    volume?: number;
    /** Resume target — `currentTime` to jump to on `loadedmetadata`. Ignored
        if ≥ duration - 5s (avoids resume-at-end). */
    resumeFrom?: number;
    /** Returns true when a newer generation has started. When true, every
        callback short-circuits and the audio is paused. */
    isStale?: () => boolean;
    /** Optional position-save callback fired throttled inside timeupdate
        (every `saveEvery` ms). */
    savePosition?: (currentTime: number) => void;
    /** Throttle window for savePosition. Default 5000. */
    saveEvery?: number;
}

interface AudioWithStopFlag extends HTMLAudioElement {
    _xotIntentionalStop?: boolean;
}

export interface AudioSession {
    /** The underlying audio element. Null after stop(). */
    el(): HTMLAudioElement | null;
    /** Native paused-state probe. */
    isPaused(): boolean;
    /** Pause playback (does not call onStopped). */
    pause(): void;
    /** Resume from paused. */
    resume(): void;
    /** Set playback rate (0.75 / 1 / 1.25 / 1.5 / 1.75 / 2). */
    setRate(rate: number): void;
    /** Set volume 0..1. */
    setVolume(volume: number): void;
    /** Seek to time in seconds, clamped to [0, duration]. */
    seekTo(seconds: number): void;
    /** Tear down the session — pause, null src, dispose, fire onStopped. */
    stop(): void;
}

/**
 * Build a new audio session bound to `url`. Returns a controller; the
 * lifecycle callbacks are wired internally to the audio element's events.
 *
 * The caller does NOT need to call `audio.load()` — the lib does that.
 * Calling `stop()` is safe + idempotent.
 */
export function createAudioSession(
    url: string,
    callbacks: AudioSessionCallbacks = {},
    opts: AudioSessionOptions = {},
): AudioSession {
    const audio = new Audio(url) as AudioWithStopFlag;
    let stopped = false;
    let readyFired = false;
    let lastSaved = 0;
    const saveEvery = opts.saveEvery ?? 5000;

    audio.playbackRate = opts.rate ?? 1;
    audio.volume       = Math.max(0, Math.min(1, opts.volume ?? 1));

    function isStale(): boolean {
        return stopped || !!opts.isStale?.();
    }

    function onLoadedMetadata(): void {
        const target = opts.resumeFrom ?? 0;
        if (target > 0 && audio.duration && target < audio.duration - 5) {
            try { audio.currentTime = target; } catch { /* some browsers reject pre-canplay seek */ }
        }
    }

    function onCanPlayThrough(): void {
        if (isStale()) { try { audio.pause(); } catch { /* ignore */ } return; }
        Promise.resolve(audio.play()).catch(() => { /* autoplay policy / stale; ignore */ });
        if (!readyFired) {
            readyFired = true;
            callbacks.onReady?.();
        }
    }

    function onTimeUpdate(): void {
        if (isStale() || !audio.duration) return;
        callbacks.onTimeUpdate?.(audio.currentTime, audio.duration);
        if (opts.savePosition) {
            const now = Date.now();
            if (now - lastSaved >= saveEvery) {
                lastSaved = now;
                opts.savePosition(audio.currentTime);
            }
        }
    }

    function onEnded(): void {
        if (isStale()) return;
        callbacks.onEnded?.();
    }

    function onError(): void {
        if (audio._xotIntentionalStop) return;
        // Natural-end errors (Safari emits error very close to duration) — ignore
        if (audio.duration > 0 && audio.currentTime >= audio.duration - NEAR_END_GUARD) return;
        if (isStale()) return;
        callbacks.onError?.();
    }

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('canplaythrough', onCanPlayThrough);
    audio.addEventListener('timeupdate',    onTimeUpdate);
    audio.addEventListener('ended',         onEnded);
    audio.addEventListener('error',         onError);

    audio.load();

    return {
        el(): HTMLAudioElement | null {
            return stopped ? null : audio;
        },
        isPaused(): boolean {
            return audio.paused;
        },
        pause(): void {
            if (stopped) return;
            try { audio.pause(); } catch { /* ignore */ }
        },
        resume(): void {
            if (stopped) return;
            Promise.resolve(audio.play()).catch(() => { /* ignore */ });
        },
        setRate(rate: number): void {
            if (stopped) return;
            audio.playbackRate = rate;
        },
        setVolume(volume: number): void {
            if (stopped) return;
            audio.volume = Math.max(0, Math.min(1, volume));
        },
        seekTo(seconds: number): void {
            if (stopped || !audio.duration) return;
            audio.currentTime = Math.max(0, Math.min(audio.duration, seconds));
        },
        stop(): void {
            if (stopped) return;
            stopped = true;
            audio._xotIntentionalStop = true;
            try { audio.pause(); } catch { /* ignore */ }
            try { audio.src = ''; } catch { /* ignore */ }
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('canplaythrough', onCanPlayThrough);
            audio.removeEventListener('timeupdate',    onTimeUpdate);
            audio.removeEventListener('ended',         onEnded);
            audio.removeEventListener('error',         onError);
            callbacks.onStopped?.();
        },
    };
}

/* ── window.XOT.article.audioSession attachment ────────────────────── */
interface XotArticleAudioSession {
    create: typeof createAudioSession;
}

declare global {
    interface Window {
        XOT?: {
            article?: {
                text?:         unknown;
                highlight?:    unknown;
                progress?:     unknown;
                share?:        unknown;
                audio?:        unknown;
                mediaSession?: unknown;
                dropoff?:      unknown;
                playerUI?:     unknown;
                audioSession?: XotArticleAudioSession;
            };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    const article = (xot.article ?? {}) as NonNullable<NonNullable<Window['XOT']>['article']>;
    article.audioSession = { create: createAudioSession };
    xot.article = article;
    window.XOT  = xot;
}
